import { FieldIds } from '@/constants/FieldIds';
import { FieldNames } from '@/constants/FieldNames';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IFormFieldRule from '@/models/form-field/IFormFieldRule';
import IFormFieldRules from '@/models/form-field/IFormFieldRules';
import IFormFields from '@/models/forms/IFormFields';
import FormsService from '@/services/2.6/FormsService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { Getters, originalValuesStore, store } from '@/store';
import OriginalValuesManager from './OriginalValuesManager';

/**
 * Manages field rules using the FormsService (QS API)
 */
export default {
    /**
     * Build an array of form field rules from the given comp's source data.
     * Ignores form filler values, comp photos and comp data w/o a field id.
     * Will use any existing original value in place of transfer value.
     * @param comp
     * @returns an array of form field rules
     */
    buildFromComp(comp: IComp, excludeNoValueAndNoSourceInitial?: boolean): IFormFieldRules {
        const fieldRules: IFormFieldRules = [];

        const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);

        if (comp.sourceData) {
            const originalValuesProperty = originalValuesStore.OriginalValuesByAddress1AndUnitNo(address1AndUnitNo);

            const compDatas = comp.sourceData.filter((cd) => cd.sourceInitial !== SourceDataInitials.FormFiller && cd.fieldName !== FieldNames.CompPhoto && cd.fieldId);
            for (const compData of compDatas) {
                if (compData.fieldId) {
                    if (typeof compData.value === 'string') {
                        if (excludeNoValueAndNoSourceInitial && !compData.value && !compData.sourceInitial) {
                            continue;
                        }

                        // Use any existing original value
                        const originalValue = originalValuesProperty?.find((ov) => ov.fieldId === compData.fieldId);

                        const fieldRule: IFormFieldRule = {
                            fieldId: compData.fieldId,
                            value: originalValue ? originalValue.originalValue : compData.value,
                        };
                        fieldRules.push(fieldRule);
                    } else {
                        console.log(`FieldRulesManager.buildFromComp(), Comp data value isn't a string[${compData.value}] on field[${compData.fieldName}]`);
                    }
                } else {
                    console.log(`FieldRulesManager.buildFromComp(), Comp data has no fieldId on field[${compData.fieldName}]`);
                }
            }
        } else {
            console.log(`FieldRulesManager.buildFromComp(), Comp[${address1AndUnitNo}] has no source data.`);
        }

        return fieldRules;
    },

    buildFromCompForFields(comp: IComp, formFields: IFormFields): IFormFieldRules {
        const fieldRules: IFormFieldRules = [];

        if (comp.sourceData) {
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
            const originalValuesProperty = OriginalValuesManager.getPropertyByAddress(comp.address);
            //console.log(`FieldRulesManager.buildFromCompForFields(), Address1AndUnitNo[${address1AndUnitNo}], Original values[${originalValuesProperty ? originalValuesProperty.length : 0}]`);

            for (const fieldData of formFields) {
                const compData = comp.sourceData.find((cd) => cd.fieldName === fieldData.fieldName);
                if (compData && compData.sourceInitial !== SourceDataInitials.FormFiller && compData.fieldName !== FieldNames.CompPhoto) {
                    // Use any existing original value
                    const originalValue = originalValuesProperty?.find((ov) => ov.fieldId === fieldData.fieldId);

                    const fieldRule: IFormFieldRule = {
                        fieldId: fieldData.fieldId,
                        value: originalValue ? originalValue.originalValue : compData.value,
                    };
                    fieldRules.push(fieldRule);
                    //console.log(`FieldRulesManager.buildFromCompForFields(), Added field rule[${JSON.stringify(fieldRule)}], original value[${originalValue ? originalValue.originalValue : ''}]`);
                }
            }
        }

        //console.log(`FieldRulesManager.buildFromCompForFields(), Field rules[${JSON.stringify(fieldRules)}]`);
        return fieldRules;
    },

    /**
     * Build a form field rules object from the given comp's source data for the given fieldId.
     * Ignores form filler values and comp photos.
     * Note: The comp data must have a fieldId value (not null or undefined).
     * Will use any existing original value in place of transfer value.
     * @param comp
     * @param fieldId
     * @returns an array of form field rules having one or zero items based on the comp and fieldId.
     */
    buildFromCompForField(comp: IComp, fieldId: number): IFormFieldRules {
        const fieldRules: IFormFieldRules = [];

        if (comp.sourceData) {
            const compData = comp.sourceData.find((cd) => cd.fieldId == fieldId);
            if (compData && compData.sourceInitial !== SourceDataInitials.FormFiller && compData.fieldId !== FieldIds.CompPhoto) {
                const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
                const originalValuesProperty = originalValuesStore.OriginalValuesByAddress1AndUnitNo(address1AndUnitNo);
                // Use any existing original value
                const originalValue = originalValuesProperty?.find((ov) => ov.fieldId === compData.fieldId);

                const fieldRule: IFormFieldRule = {
                    fieldId: fieldId,
                    value: originalValue ? originalValue.originalValue : compData.value,
                };
                fieldRules.push(fieldRule);
            }
        }
        return fieldRules;
    },

    /**
     * Run field rules using the FormsService (QS API) on the given field rules.
     * The resulting form field rules are associated with the comp identified by the given comp address.
     * The comp address is necessary to maintain the original values store state.
     * @param fieldRules
     * @returns
     */
    async runFieldRulesForComp(compAddress: ICompAddress, fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
        if (fieldRules.length > 0) {
            //console.log(`FieldRulesManager.runFieldRulesForComp(), Submitting field rules[${JSON.stringify(fieldRules)}]`);

            const formFieldRules = await runFieldRulesService(fieldRules);
            if (formFieldRules) {
                //console.log(`FieldRulesManager.runFieldRulesForComp(), Received field rules[${JSON.stringify(formFieldRules)}]`);

                // Maintain the original values store state
                OriginalValuesManager.updateFromFieldRules(compAddress, fieldRules, formFieldRules);
            }
            return formFieldRules;
        }
        return null;
    },

    /**
     * Run field rules using the FormsService (QS API) on the given field rules.
     * The resulting form field rules are associated with the active comp.
     * The comp address is necessary to maintain the original values store state.
     * @param fieldRules
     * @returns
     */
    async runFieldRules(fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
        const activeComp: IComp = store.getters[Getters.CompAddresses.ActiveComp];

        const formFieldRules = await this.runFieldRulesForComp(activeComp.address, fieldRules);
        return formFieldRules;
    },

    /**
     * Run field rules using the FormsService (QS API) on the given field rule.
     * The resulting form field rules are associated with the active comp.
     * The comp address is necessary to maintain the original values store state.
     * @param fieldRule
     * @returns
     */
    async runFieldRule(fieldRule: IFormFieldRule): Promise<IFormFieldRules | null> {
        const fieldRules: IFormFieldRules = [];
        fieldRules.push(fieldRule);

        // console.log(`FieldRulesManager.runFieldRule(), Submitting field rules[${JSON.stringify(fieldRules)}]`);

        const formFieldRules = await runFieldRulesService(fieldRules);
        if (formFieldRules) {
            // console.log(`FieldRulesManager.runFieldRule(), Received field rules[${JSON.stringify(formFieldRules)}]`);

            // Maintain the original values store state
            const activeComp: IComp = store.getters[Getters.CompAddresses.ActiveComp];
            await OriginalValuesManager.updateFromFieldRules(activeComp.address, fieldRules, formFieldRules);
        }

        return formFieldRules;
    },
};

async function runFieldRulesService(fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    const service = new FormsService(webEnvConfig);

    const formFieldRules = await service.runFieldRules(fieldRules);

    return formFieldRules;
}
